import React from 'react';
import { Monitor, Users, GitMerge } from 'lucide-react';
import { CalendarEvent } from '../../types/crm';
import { formatCalendarSessionTitle, isCalendarEventCancelled, spotsAvailabilityClass, stripCalendarNoteMeta } from './trainingCalendarUtils';

interface TrainingCalendarEventCardProps {
  event: CalendarEvent;
  linesBudget: number;
  calendarView: 'month' | 'week' | 'day';
  onBookingClick: (event: CalendarEvent, e: React.MouseEvent) => void;
  /** Expanded day / day modal: larger tile layout instead of compact row strip */
  variant?: 'compact' | 'tile';
  /** Highlight one tile (e.g. last focused) with elevated “popped” styling */
  isSelected?: boolean;
  /** Runs before `onBookingClick` when the user activates the card (e.g. to track selection) */
  onSelect?: () => void;
}

const STATUS_COLORS: Record<string, string> = {
  confirmed: 'bg-emerald-500',
  pending: 'bg-amber-400',
  cancelled: 'bg-red-400',
  completed: 'bg-slate-400',
};

const DELIVERY_MODE_CONFIG: Record<string, { label: string; icon: React.ReactNode; classes: string }> = {
  online: {
    label: 'Online',
    icon: <Monitor className="w-2.5 h-2.5" />,
    classes: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300 border-sky-200 dark:border-sky-800',
  },
  blended: {
    label: 'Blended',
    icon: <GitMerge className="w-2.5 h-2.5" />,
    classes: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300 border-teal-200 dark:border-teal-800',
  },
  in_class: {
    label: 'In Class',
    icon: <Users className="w-2.5 h-2.5" />,
    classes: 'bg-slate-100 text-slate-600 dark:bg-slate-700/40 dark:text-slate-300 border-slate-200 dark:border-slate-600',
  },
};

function statusDot(status?: string) {
  const color = STATUS_COLORS[status?.toLowerCase() ?? ''] ?? 'bg-blue-400';
  return <span className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 mt-0.5 ${color}`} />;
}

function DeliveryBadge({ mode, large }: { mode?: string | null; large?: boolean }) {
  if (!mode) return null;
  const config = DELIVERY_MODE_CONFIG[mode];
  if (!config) return null;
  const icon = large
    ? React.cloneElement(config.icon as React.ReactElement, { className: 'w-3.5 h-3.5' })
    : config.icon;
  return (
    <span
      className={`inline-flex items-center gap-0.5 rounded border font-semibold leading-none ${large ? 'px-1.5 py-1 text-xs' : 'px-1 py-0.5 text-[9px]'} ${config.classes}`}
    >
      {icon}
      {config.label}
    </span>
  );
}

export function TrainingCalendarEventCard({
  event,
  linesBudget,
  calendarView,
  onBookingClick,
  variant = 'compact',
  isSelected = false,
  onSelect,
}: TrainingCalendarEventCardProps) {
  const tile = variant === 'tile';
  const isWeek = calendarView === 'week';
  const isCancelled = isCalendarEventCancelled(event);
  const title = formatCalendarSessionTitle(event);
  const company = event.companyName;
  const shouldShowCompany =
    !!company &&
    company.trim().length > 0 &&
    !title.toLowerCase().includes(company.toLowerCase()) &&
    title.toLowerCase() !== company.toLowerCase();
  const venueShort =
    ((event.resource as Record<string, unknown>)?.locationShortLabel as string | undefined) || undefined;
  const venueFull = event.resource?.locationName;
  const instructor = event.resource?.instructorName;
  const interpreter = event.resource?.interpreterName;
  const booked = event.resource?.bookedCount ?? event.attendeeCount;
  const capacity = event.resource?.capacity;
  const deliveryMode = event.resource?.deliveryMode;
  const referenceCode = event.resource?.sessionReferenceCode;
  const sessionNotes = stripCalendarNoteMeta(
    event.resource?.sessionNotes || event.resource?.sessionOtherInfo || event.resource?.notes
  );
  const time = event.startTime
    ? event.endTime
      ? `${event.startTime}–${event.endTime}`
      : event.startTime
    : undefined;

  const titleClass = tile
    ? isCancelled
      ? 'text-sm sm:text-base font-bold text-red-900 dark:text-red-100 line-through decoration-red-400/70'
      : 'text-sm sm:text-base font-bold text-blue-900 dark:text-blue-100'
    : `font-semibold leading-tight truncate ${isWeek ? 'text-[11px]' : 'text-[10px]'} ${
        isCancelled
          ? 'text-red-800 dark:text-red-200 line-through decoration-red-400/70'
          : 'text-blue-800 dark:text-blue-200'
      }`;
  const metaSm = tile ? 'text-xs sm:text-sm' : 'text-[10px]';
  const metaXs = tile ? 'text-[11px] sm:text-xs' : 'text-[9px]';
  const pillMeta = tile ? 'text-xs font-medium' : 'text-[10px]';

  const tileBtn = tile
    ? isCancelled
      ? `w-full text-left rounded-xl border-2 transition-all duration-200 ease-out group flex flex-col gap-1.5
        ${isSelected
          ? 'z-20 scale-[1.05] sm:scale-[1.06] -translate-y-1.5 shadow-2xl border-red-500 bg-gradient-to-br from-red-50 via-red-50/90 to-rose-50/80 dark:from-red-950/60 dark:via-red-950/50 dark:to-rose-950/40 dark:border-red-400 ring-2 ring-red-400/70 dark:ring-red-500/50'
          : 'border-red-400 dark:border-red-700 bg-red-50/95 dark:bg-red-950/35 shadow-md hover:-translate-y-0.5 hover:shadow-lg hover:border-red-500 dark:hover:border-red-500'}
        px-3 py-2.5 min-h-[5.25rem] active:scale-[0.99]`
      : `w-full text-left rounded-xl border-2 transition-all duration-200 ease-out group flex flex-col gap-1.5
        ${isSelected
          ? 'z-20 scale-[1.05] sm:scale-[1.06] -translate-y-1.5 shadow-2xl border-blue-500 bg-gradient-to-br from-white via-blue-50/90 to-indigo-50/80 dark:from-slate-800 dark:via-blue-950/50 dark:to-indigo-950/40 dark:border-blue-400 ring-2 ring-blue-400/70 dark:ring-blue-500/50'
          : 'border-blue-200/90 dark:border-blue-800/90 bg-blue-50/90 dark:bg-blue-950/25 shadow-md hover:-translate-y-0.5 hover:shadow-lg hover:border-blue-400 dark:hover:border-blue-500'}
        px-3 py-2.5 min-h-[5.25rem] active:scale-[0.99]`
    : isCancelled
      ? `w-full text-left rounded-md px-1.5 py-1 border-2 transition-all duration-150 group
        bg-red-50 dark:bg-red-900/35 border-red-400 dark:border-red-700
        hover:bg-red-100 dark:hover:bg-red-900/50 hover:border-red-500 dark:hover:border-red-500
        hover:shadow-sm active:scale-[0.98]`
      : `w-full text-left rounded-md px-1.5 py-1 border transition-all duration-150 group
        bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800
        hover:bg-blue-100 dark:hover:bg-blue-900/50 hover:border-blue-400 dark:hover:border-blue-600
        hover:shadow-sm active:scale-[0.98]`;

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onSelect?.();
        onBookingClick(event, e);
      }}
      className={tileBtn}
    >
      <div className={`flex items-start gap-1 ${tile ? 'gap-2' : ''}`}>
        <span className={tile ? 'scale-125 mt-0.5' : ''}>{statusDot(event.status)}</span>
        <div className={`min-w-0 flex-1 ${tile ? 'flex flex-col gap-1' : ''}`}>
          <div className="flex items-start justify-between gap-1">
            <div className={`${titleClass} ${tile ? 'line-clamp-2' : 'truncate'}`}>
              {title}
            </div>
            <div className="flex shrink-0 flex-col items-end gap-0.5">
              {isCancelled ? (
                <span className={`rounded border font-bold uppercase tracking-wide text-red-700 dark:text-red-200 border-red-300 dark:border-red-700 bg-red-100 dark:bg-red-900/50 ${tile ? 'px-1.5 py-0.5 text-[10px]' : 'px-1 py-0.5 text-[8px]'}`}>
                  Cancelled
                </span>
              ) : null}
              <DeliveryBadge mode={deliveryMode} large={tile} />
            </div>
          </div>

          {linesBudget >= 2 && shouldShowCompany && (
            <div className={`${metaSm} text-slate-600 dark:text-slate-400 ${tile ? 'line-clamp-2 leading-snug' : 'truncate leading-tight'}`}>
              {company}
            </div>
          )}

          {linesBudget >= 3 && (
            <div className={`flex flex-wrap items-center gap-x-2 ${tile ? 'mt-0.5' : 'mt-0.5'} min-w-0`}>
              {time && (
                <span className={`${pillMeta} text-slate-600 dark:text-slate-300`}>{time}</span>
              )}
              {(venueShort || venueFull) && (
                <span
                  className={`inline-flex max-w-full sm:max-w-[200px] items-center rounded border border-slate-200 dark:border-slate-600 px-1.5 ${metaXs} font-medium text-slate-600 dark:text-slate-300 truncate`}
                  title={venueFull || venueShort}
                >
                  [{venueShort || venueFull}]
                </span>
              )}
              {linesBudget < 4 && booked != null && (
                <span className={`${pillMeta} font-semibold ${spotsAvailabilityClass(event)}`}>
                  {capacity != null && capacity > 0 ? `${booked}/${capacity}` : `${booked} booked`}
                </span>
              )}
            </div>
          )}

          {linesBudget >= 4 && (
            <div className={`flex flex-wrap gap-x-2 ${tile ? 'mt-0.5' : 'mt-0.5'}`}>
              {instructor && (
                <span className={`${metaSm} text-slate-600 dark:text-slate-400 truncate`}>{instructor}</span>
              )}
              {interpreter && (
                <span className={`${metaSm} text-slate-600 dark:text-slate-400 truncate`}>| {interpreter}</span>
              )}
              {booked != null && (
                <span className={`${pillMeta} font-semibold ${spotsAvailabilityClass(event)}`}>
                  {capacity != null && capacity > 0 ? `${booked}/${capacity} booked` : `${booked} booked`}
                </span>
              )}
            </div>
          )}

          {linesBudget >= 5 && referenceCode && (
            <div className={`${tile ? 'mt-1' : 'mt-0.5'} flex flex-wrap items-center gap-1`}>
              <span className={`rounded border border-slate-200 bg-white/70 px-1.5 py-0.5 ${metaXs} font-semibold text-slate-600 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-300`}>
                {referenceCode}
              </span>
            </div>
          )}

          {linesBudget >= 6 && sessionNotes && (
            <div className={`${tile ? 'mt-0.5 line-clamp-2' : 'mt-0.5 truncate'} ${metaSm} text-slate-500 dark:text-slate-400`} title={sessionNotes}>
              Note: {sessionNotes}
            </div>
          )}
        </div>
      </div>
    </button>
  );
}
